/**
 * @fileoverview Finder-based Documents Library (library redesign).
 * Replaces the dropzone + table layout. Double-click opens the existing
 * DocumentDetail sheet; the vendored Finder's built-in open behavior is
 * overridden via onFileOpen. Files/folders dropped anywhere on the Finder
 * (or picked via "Add Files" in the toolbar) upload into the currently
 * open folder. A category filter / search switches the Finder into a flat
 * results mode across all folders.
 *
 * The Finder's Morgan extensions are wired here: toolbar slot content
 * (LibraryToolbarStart/End render inside the Finder's own toolbar row,
 * with the built-in search/filter suppressed), list-view columns
 * (category select, processing status, uploader, flat-mode location chip),
 * item context menus, internal drag-moves, folder-targeted OS drops, and
 * the move/rename/delete dialogs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import { ConvexError } from "convex/values";
import { toast } from "sonner";
import { Cloud } from "lucide-react";
import {
  FileSystem,
  type FileSystemFileItem,
  type FileSystemItem,
} from "@/ui/composed/file-system/file-system";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import {
  CATEGORY_LABELS,
  FAILED_FILTER_KEY,
  FAILED_FILTER_LABEL,
  getOverallStatus,
} from "../constants";
import { DocumentDetail } from "../documents/DocumentDetail";
import { ProcessingStatusBadge } from "../documents/ProcessingStatusBadge";
import { buildLibraryItems, type LibraryItemMeta } from "./manifest";
import { LibraryFilePreview } from "./LibraryFilePreview";
import { libraryPreviewKind } from "./LibraryFilePreview.helpers";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { FolderPickerDialog } from "./FolderPickerDialog";
import {
  LibraryItemMenu,
  type LibraryMenuActions,
  type LibraryMenuItem,
} from "./itemMenus";
import {
  LibraryToolbarEnd,
  LibraryToolbarStart,
  NewFolderDialog,
} from "./LibraryToolbar";
import { loadPdfPreview } from "./pdfThumbnails";
import { RenameDialog } from "./RenameDialog";
import { useLibraryUpload } from "./useLibraryUpload";

interface LibraryViewProps {
  jobId: Id<"jobs">;
  isViewer: boolean;
  selectedDocumentId: Id<"documents"> | null;
  onSelectDocument: (id: Id<"documents"> | null) => void;
}

function isLibraryDocumentFailed(doc: {
  extractionStatus?: string;
  classificationStatus?: string;
  indexingStatus?: string;
}): boolean {
  return (
    getOverallStatus(
      doc.extractionStatus,
      doc.classificationStatus,
      doc.indexingStatus,
    ) === "failed"
  );
}

/**
 * Documents Library rendered as a Finder over the job's folders and
 * documents. Owns the DocumentDetail sheet; double-clicking a file always
 * opens the sheet (never window.open or the built-in image dialog).
 */
export function LibraryView({
  jobId,
  isViewer,
  selectedDocumentId,
  onSelectDocument,
}: LibraryViewProps) {
  const convex = useConvex();

  const { data: folders } = useQuery(
    convexQuery(api.jobs.gatherInformation.folders.queries.listFolders, {
      jobId,
    }),
  );

  const { data: documents } = useQuery(
    convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
  );

  // Drive connection (for delete-confirm provider copy).
  const { data: integrations } = useQuery(
    convexQuery(api.integrations.workOsSync.queries.getJobIntegrations, {
      jobId,
    }),
  );
  const providerName = useMemo(() => {
    const connection = (integrations ?? []).find(
      (c) =>
        (c.provider === "microsoft-onedrive" ||
          c.provider === "microsoft-sharepoint") &&
        c.status === "active",
    );
    if (!connection) return null;
    return connection.provider === "microsoft-onedrive"
      ? "OneDrive"
      : "SharePoint";
  }, [integrations]);

  // Close the detail sheet if the selected document disappears (deleted
  // elsewhere, or removed by a sync run).
  useEffect(() => {
    if (
      selectedDocumentId &&
      documents !== undefined &&
      !documents.some((d) => d._id === selectedDocumentId)
    ) {
      onSelectDocument(null);
    }
  }, [selectedDocumentId, documents, onSelectDocument]);

  // --- Flat filter/search mode -------------------------------------------
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const trimmedSearch = searchQuery.trim().toLowerCase();
  const isFlat = categoryFilter !== "all" || trimmedSearch !== "";

  const failedCount = useMemo(() => {
    return (documents ?? []).filter(isLibraryDocumentFailed).length;
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const docs = documents ?? [];
    if (!isFlat) return docs;
    return docs.filter((doc) => {
      if (categoryFilter === FAILED_FILTER_KEY) {
        if (!isLibraryDocumentFailed(doc)) return false;
      } else if (categoryFilter !== "all" && doc.category !== categoryFilter) {
        return false;
      }
      if (trimmedSearch) {
        const haystack =
          `${doc.fileName} ${doc.documentType ?? ""}`.toLowerCase();
        if (!haystack.includes(trimmedSearch)) return false;
      }
      return true;
    });
  }, [documents, isFlat, categoryFilter, trimmedSearch]);

  const { items, metaByPath } = useMemo(
    () =>
      isFlat
        ? buildLibraryItems([], filteredDocuments, { flat: true })
        : buildLibraryItems(folders ?? [], documents ?? []),
    [isFlat, filteredDocuments, folders, documents],
  );

  // The folder currently open in the Finder; uploads land here.
  // metaByPath keys folders with a trailing slash, matching the Finder's
  // current-path format ("" at the root → upload to the Library root).
  const [currentPath, setCurrentPath] = useState("");
  const currentFolderId = useMemo(() => {
    if (!currentPath) return undefined;
    const normalized = currentPath.endsWith("/")
      ? currentPath
      : currentPath + "/";
    return metaByPath.get(normalized)?.folderId as Id<"folders"> | undefined;
  }, [currentPath, metaByPath]);

  // Flat mode has no real folder context — uploads target the Library root
  // there, matching the "All Documents" semantics.
  const uploadTargetFolderId = isFlat ? undefined : currentFolderId;

  const { handleDrop, handlePickedFiles, uploadingCount } =
    useLibraryUpload(jobId);

  // Drop-highlight ring while files are dragged over the Finder. A depth
  // counter survives dragenter/dragleave pairs fired by child elements.
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const isFileDrag = useCallback(
    (e: React.DragEvent) => e.dataTransfer.types.includes("Files"),
    [],
  );

  // --- Finder extensions: selection, menus, dialogs ----------------------

  /** Resolve a Finder item to its manifest-backed menu shape. */
  const toMenuItem = useCallback(
    (item: FileSystemItem): LibraryMenuItem => {
      const path =
        item.kind === "folder" && !item.path.endsWith("/")
          ? `${item.path}/`
          : item.path;
      const meta: LibraryItemMeta | undefined = metaByPath.get(path);
      return {
        kind: item.kind === "folder" ? "folder" : "file",
        path,
        name: item.name ?? path,
        documentId: meta?.documentId as Id<"documents"> | undefined,
        folderId: meta?.folderId as Id<"folders"> | undefined,
        url: item.kind === "file" ? item.url : undefined,
        category: meta?.category,
        isSynced: meta?.isSynced ?? false,
      };
    },
    [metaByPath],
  );

  const [selectedItems, setSelectedItems] = useState<LibraryMenuItem[]>([]);
  const handleSelectionItemsChange = useCallback(
    (fsItems: FileSystemItem[]) => setSelectedItems(fsItems.map(toMenuItem)),
    [toMenuItem],
  );

  // Dialog state outlives the context menu that opened it.
  const [moveItems, setMoveItems] = useState<LibraryMenuItem[] | null>(null);
  const [deleteItems, setDeleteItems] = useState<LibraryMenuItem[] | null>(
    null,
  );
  const [renameItem, setRenameItem] = useState<LibraryMenuItem | null>(null);
  const [subfolderParentId, setSubfolderParentId] =
    useState<Id<"folders"> | null>(null);

  // Tree jump used by the flat-mode location chip: clear the filter and
  // remount the Finder opened at the target folder (defaultPath is only
  // read on mount; the nonce forces a remount for repeat jumps).
  const [jump, setJump] = useState<{ path: string; nonce: number }>({
    path: "",
    nonce: 0,
  });
  const jumpToLocation = useCallback((location: string) => {
    setCategoryFilter("all");
    setSearchQuery("");
    setJump((previous) => ({
      path:
        location === "Library root" || location === "/"
          ? ""
          : `${location.replace(/^\//, "")}/`,
      nonce: previous.nonce + 1,
    }));
  }, []);

  const updateCategory = useCallback(
    async (documentIds: Id<"documents">[], category: string) => {
      try {
        for (const documentId of documentIds) {
          await convex.mutation(
            api.jobs.gatherInformation.documents.mutations
              .updateDocumentCategory,
            { documentId, category: category as DocumentCategory },
          );
        }
      } catch {
        toast.error("Failed to update category");
      }
    },
    [convex],
  );

  const menuActions = useMemo<LibraryMenuActions>(
    () => ({
      onOpenDocument: (item) => {
        if (item.documentId) onSelectDocument(item.documentId);
      },
      onDownload: (item) => {
        if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
      },
      onMoveTo: (menuItems) => setMoveItems(menuItems),
      onSetCategory: (files, category) => {
        const ids = files
          .map((f) => f.documentId)
          .filter((id): id is Id<"documents"> => id !== undefined);
        if (ids.length > 0) void updateCategory(ids, category);
      },
      onRename: (item) => setRenameItem(item),
      onReprocess: (files) => {
        void (async () => {
          const ids = files
            .map((f) => f.documentId)
            .filter((id): id is Id<"documents"> => id !== undefined);
          try {
            for (const documentId of ids) {
              await convex.mutation(
                api.jobs.gatherInformation.documents.mutations
                  .reprocessDocument,
                { documentId },
              );
            }
            toast.success(
              `Re-processing ${ids.length} document${ids.length === 1 ? "" : "s"}…`,
            );
          } catch {
            toast.error("Failed to start re-processing");
          }
        })();
      },
      onDelete: (menuItems) => setDeleteItems(menuItems),
      onNewSubfolder: (item) => {
        if (item.folderId) setSubfolderParentId(item.folderId);
      },
    }),
    [convex, onSelectDocument, updateCategory],
  );

  const renderItemMenu = useCallback(
    (fsItems: FileSystemItem[]) => (
      <LibraryItemMenu items={fsItems.map(toMenuItem)} actions={menuActions} />
    ),
    [menuActions, toMenuItem],
  );

  // Internal drag-move: documents move in one mutation; folders move one at
  // a time (cross-boundary moves surface the server's ConvexError).
  const handleMoveItems = useCallback(
    (fsItems: FileSystemItem[], targetFolderPath: string) => {
      void (async () => {
        const moved = fsItems.map(toMenuItem);
        const folderId = targetFolderPath
          ? (metaByPath.get(targetFolderPath)?.folderId as
              Id<"folders"> | undefined)
          : undefined;
        // A non-root target that doesn't resolve to a real folder means the
        // manifest and view disagree — do nothing rather than move to root.
        if (targetFolderPath && !folderId) return;
        try {
          const documentIds = moved
            .filter((m) => m.kind === "file")
            .map((m) => m.documentId)
            .filter((id): id is Id<"documents"> => id !== undefined);
          if (documentIds.length > 0) {
            await convex.mutation(
              api.jobs.gatherInformation.documents.mutations.moveDocuments,
              { documentIds, folderId },
            );
          }
          for (const folder of moved.filter((m) => m.kind === "folder")) {
            if (!folder.folderId) continue;
            await convex.mutation(
              api.jobs.gatherInformation.folders.mutations.moveFolder,
              { folderId: folder.folderId, newParentFolderId: folderId },
            );
          }
        } catch (err) {
          toast.error(
            err instanceof ConvexError && typeof err.data === "string"
              ? err.data
              : "Failed to move items",
          );
        }
      })();
    },
    [convex, metaByPath, toMenuItem],
  );

  const handleExternalDropOnFolder = useCallback(
    (dataTransfer: DataTransfer, targetFolderPath: string) => {
      const folderId = targetFolderPath
        ? (metaByPath.get(targetFolderPath)?.folderId as
            Id<"folders"> | undefined)
        : undefined;
      void handleDrop(dataTransfer, folderId);
    },
    [handleDrop, metaByPath],
  );

  // Lazy first-page thumbnails: manifest items for PDFs declare
  // `previewPageCount: 1`, so the Finder's tiles call back here for the
  // rendered page. The manifest carries the document's Convex URL and MIME
  // type on the item itself, so no meta lookup is needed (and the callback
  // stays referentially stable across manifest rebuilds).
  const loadPreviewImageUrl = useCallback(async (file: FileSystemFileItem) => {
    if (file.contentType === "application/pdf" && file.url) {
      return loadPdfPreview(file.url);
    }
    return null;
  }, []);

  // --- List-view columns ---------------------------------------------------

  const itemColumnsHeader = useMemo(
    () => (
      <>
        <span className="w-44 shrink-0">Category</span>
        <span className="w-40 shrink-0">Status</span>
        <span className="w-24 shrink-0">Uploaded by</span>
        {isFlat && <span className="w-36 shrink-0">Location</span>}
      </>
    ),
    [isFlat],
  );

  const renderItemColumns = useCallback(
    (item: FileSystemItem) => {
      const path =
        item.kind === "folder" && !item.path.endsWith("/")
          ? `${item.path}/`
          : item.path;
      const meta = metaByPath.get(path);
      const isFile = item.kind === "file";
      const documentId = meta?.documentId as Id<"documents"> | undefined;

      return (
        <>
          <span
            className="w-44 shrink-0"
            // Keep select interactions from also toggling row selection.
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            {isFile &&
              documentId &&
              (isViewer ? (
                <span className="text-xs text-muted-foreground">
                  {CATEGORY_LABELS[meta?.category ?? ""] ?? meta?.category}
                </span>
              ) : (
                <Select
                  value={meta?.category}
                  onValueChange={(value) =>
                    void updateCategory([documentId], value)
                  }
                >
                  <SelectTrigger className="h-7 min-h-7 w-full text-xs text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS)
                      .filter(([key]) => key !== "all")
                      .map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              ))}
          </span>
          <span className="flex w-40 shrink-0 items-center">
            {isFile ? (
              <ProcessingStatusBadge
                extractionStatus={meta?.extractionStatus}
                classificationStatus={meta?.classificationStatus}
                indexingStatus={meta?.indexingStatus}
                extractionFailureClass={meta?.extractionFailureClass}
                classificationFailureClass={meta?.classificationFailureClass}
                indexingFailureClass={meta?.indexingFailureClass}
                extractionError={meta?.extractionError}
                classificationError={meta?.classificationError}
                indexingError={meta?.indexingError}
              />
            ) : (
              meta?.isSynced && (
                <Badge color="sky" variant="soft" className="gap-1">
                  <Cloud className="h-3 w-3" />
                  Synced
                </Badge>
              )
            )}
          </span>
          <span className="w-24 shrink-0 truncate text-xs text-muted-foreground">
            {isFile ? meta?.uploaderName : null}
          </span>
          {isFlat && (
            <span
              className="w-36 shrink-0"
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              {isFile && meta?.location && (
                <button
                  type="button"
                  title="Show in folder"
                  onClick={() => jumpToLocation(meta.location ?? "/")}
                  className="max-w-full truncate rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  {meta.location === "Library root"
                    ? "Library root"
                    : meta.location}
                </button>
              )}
            </span>
          )}
        </>
      );
    },
    [isFlat, isViewer, jumpToLocation, metaByPath, updateCategory],
  );

  const getItemClassName = useCallback(
    (item: FileSystemItem) => {
      if (item.kind !== "file") return undefined;
      const meta = metaByPath.get(item.path);
      if (!meta || !isLibraryDocumentFailed(meta)) return undefined;
      return "bg-destructive/5 text-destructive hover:bg-destructive/10";
    },
    [metaByPath],
  );

  // --- Columns-view preview pane: Category + Status -----------------------
  const renderPreviewDetails = useCallback(
    (file: FileSystemFileItem) => {
      const meta = metaByPath.get(file.path);
      if (!meta?.documentId) return null;

      const categoryLabel =
        CATEGORY_LABELS[meta.category ?? ""] ?? meta.category;

      return (
        <div className="border-t pb-2 pt-3">
          <div className="mb-1.5 text-xs font-semibold">Document</div>
          <dl className="space-y-1.5">
            <div className="flex items-center justify-between gap-3 text-xs">
              <dt className="shrink-0 text-muted-foreground">Category</dt>
              <dd className="truncate text-right">
                {categoryLabel ?? "Uncategorized"}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs">
              <dt className="shrink-0 text-muted-foreground">Status</dt>
              <dd className="flex justify-end">
                <ProcessingStatusBadge
                  extractionStatus={meta.extractionStatus}
                  classificationStatus={meta.classificationStatus}
                  indexingStatus={meta.indexingStatus}
                  extractionFailureClass={meta.extractionFailureClass}
                  classificationFailureClass={meta.classificationFailureClass}
                  indexingFailureClass={meta.indexingFailureClass}
                  extractionError={meta.extractionError}
                  classificationError={meta.classificationError}
                  indexingError={meta.indexingError}
                />
              </dd>
            </div>
          </dl>
        </div>
      );
    },
    [metaByPath],
  );

  // --- Large preview surfaces: live Word/Excel/text document previews ------
  // Returns null for images/PDFs/unsupported types so the Finder falls back
  // to its own thumbnail pipeline. Only ever mounted for the single selected
  // file in the columns preview pane / gallery stage, never per tile.
  const renderLargePreview = useCallback((file: FileSystemFileItem) => {
    if (!file.url || !libraryPreviewKind(file)) return null;
    return <LibraryFilePreview file={file} />;
  }, []);

  const isLoading = folders === undefined || documents === undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {isFlat && !isLoading && (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            {filteredDocuments.length} result
            {filteredDocuments.length === 1 ? "" : "s"} across all folders
          </span>
          {categoryFilter !== "all" && (
            <Badge
              variant={
                categoryFilter === FAILED_FILTER_KEY
                  ? "destructive"
                  : "secondary"
              }
            >
              {categoryFilter === FAILED_FILTER_KEY
                ? FAILED_FILTER_LABEL
                : (CATEGORY_LABELS[categoryFilter] ?? categoryFilter)}
            </Badge>
          )}
          {selectedItems.length > 1 && (
            <span>· {selectedItems.length} selected</span>
          )}
        </div>
      )}

      {isLoading ? (
        <LibrarySkeleton />
      ) : (
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col rounded-lg",
            isDragOver && "ring-2 ring-primary/50",
          )}
          onDragOver={
            isViewer
              ? undefined
              : (e) => {
                  if (isFileDrag(e)) e.preventDefault();
                }
          }
          onDragEnter={
            isViewer
              ? undefined
              : (e) => {
                  if (!isFileDrag(e)) return;
                  e.preventDefault();
                  dragDepthRef.current++;
                  setIsDragOver(true);
                }
          }
          onDragLeave={
            isViewer
              ? undefined
              : (e) => {
                  if (!isFileDrag(e)) return;
                  dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
                  if (dragDepthRef.current === 0) setIsDragOver(false);
                }
          }
          onDrop={
            isViewer
              ? undefined
              : (e) => {
                  if (!isFileDrag(e)) return;
                  e.preventDefault();
                  dragDepthRef.current = 0;
                  setIsDragOver(false);
                  void handleDrop(e.dataTransfer, uploadTargetFolderId);
                }
          }
        >
          <FileSystem
            // Remount when switching modes so navigation state (current
            // path, selection) resets cleanly between tree and flat views;
            // the jump nonce remounts onto a location-chip target.
            key={isFlat ? "flat" : `tree-${jump.nonce}`}
            items={items}
            defaultView="list"
            defaultPath={isFlat ? "" : jump.path}
            className="min-h-0 flex-1"
            // Morgan's toolbar lives in the Finder's own toolbar row: the
            // wide cross-folder search on the left (the primary control),
            // and New Folder / Add Files + category filter + status pills
            // + bulk menu on the right. The built-in search and file-type
            // filter are suppressed — the category filter and cross-folder
            // search replace them.
            hideSearch
            hideFilter
            toolbarStart={
              <LibraryToolbarStart
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
              />
            }
            toolbarEnd={
              <LibraryToolbarEnd
                jobId={jobId}
                isViewer={isViewer}
                currentFolderId={uploadTargetFolderId}
                onPickFiles={(files) =>
                  void handlePickedFiles(files, uploadTargetFolderId)
                }
                categoryFilter={categoryFilter}
                onCategoryFilterChange={setCategoryFilter}
                failedCount={failedCount}
                uploadingCount={uploadingCount}
              />
            }
            onCurrentPathChange={setCurrentPath}
            onFileOpen={(file) => {
              const meta = metaByPath.get(file.path);
              if (meta?.documentId) {
                onSelectDocument(meta.documentId as Id<"documents">);
              }
            }}
            readOnly={isViewer}
            loadPreviewImageUrl={loadPreviewImageUrl}
            itemColumnsHeader={itemColumnsHeader}
            renderItemColumns={renderItemColumns}
            getItemClassName={getItemClassName}
            renderPreviewDetails={renderPreviewDetails}
            renderLargePreview={renderLargePreview}
            renderItemMenu={renderItemMenu}
            onSelectionItemsChange={handleSelectionItemsChange}
            onMoveItems={isViewer ? undefined : handleMoveItems}
            onExternalDropOnFolder={
              isViewer ? undefined : handleExternalDropOnFolder
            }
          />
        </div>
      )}

      <DocumentDetail
        documentId={selectedDocumentId}
        isViewer={isViewer}
        open={selectedDocumentId !== null}
        onClose={() => onSelectDocument(null)}
      />

      {/* Item-action dialogs (opened from the context menus). */}
      <FolderPickerDialog
        jobId={jobId}
        open={moveItems !== null}
        onOpenChange={(open) => {
          if (!open) setMoveItems(null);
        }}
        documentIds={(moveItems ?? [])
          .map((m) => m.documentId)
          .filter((id): id is Id<"documents"> => id !== undefined)}
        folderIds={(moveItems ?? [])
          .map((m) => m.folderId)
          .filter((id): id is Id<"folders"> => id !== undefined)}
      />
      <DeleteConfirmDialog
        open={deleteItems !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteItems(null);
        }}
        items={deleteItems ?? []}
        providerName={providerName}
      />
      <RenameDialog
        open={renameItem !== null}
        onOpenChange={(open) => {
          if (!open) setRenameItem(null);
        }}
        item={renameItem}
      />
      <NewFolderDialog
        jobId={jobId}
        parentFolderId={subfolderParentId ?? undefined}
        open={subfolderParentId !== null}
        onOpenChange={(open) => {
          if (!open) setSubfolderParentId(null);
        }}
      />
    </div>
  );
}

/**
 * Loading skeleton shown while folders/documents queries resolve. Mirrors
 * the Finder layout (toolbar strip + list rows) with a simple pulse.
 */
function LibrarySkeleton() {
  return (
    <div className="flex min-h-0 flex-1 animate-pulse flex-col gap-0 overflow-hidden rounded-lg border bg-background">
      <div className="h-12 shrink-0 border-b bg-muted/50" />
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-10 shrink-0 border-b bg-background last:border-b-0"
        />
      ))}
    </div>
  );
}
